import React, { useState } from 'react';
import { Form, Input, InputNumber, Button, DatePicker, Upload, Select, message, Checkbox } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { TRANSACTION_REFERENCE } from '../types/accountPurposes';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../../AuthProvider';
import useRecords from './useRecords';
import { useEffect } from 'react';
import { fetchChartOfAccountsList } from '../services/ChartOfAccountsService';

const { Option } = Select;

// showAllPurposes: if true, show all transaction references; if false, show only the four for Bills/Expense
const RecordForm = ({ type, showAllPurposes = false, onRecordAdded, initialValues = {}, paidDisabled = false }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [chartAccounts, setChartAccounts] = useState([]);
  const [selectedCoaId, setSelectedCoaId] = useState(null);
  const [selectedCoaDetails, setSelectedCoaDetails] = useState(null);
  
  const authContext = useAuth();
  const { create, loading } = useRecords(type);

  // Fetch Chart of Accounts on mount
  useEffect(() => {
    const fetchCoA = async () => {
      try {
        const res = await fetchChartOfAccountsList(authContext.token);
        setChartAccounts(res.data || []);
      } catch (e) {
        setChartAccounts([]);
      }
    };
    fetchCoA();
  }, [authContext.token]);

  // Calculate GST (10% of amount, if GST applies)
  const calculateGST = (amount, isGstFree) => {
    if (isGstFree) return 0; // No GST calculation if item is flagged GST-Free
    const amt = parseFloat(amount);
    if (isNaN(amt)) return 0;
    return amt / 11;
  };

  const onFinish = async (values) => {
    const safe = (v) => v !== undefined && v !== null ? v : '';
    
    const calculateAmountExclGST = (amount, isGstFree) => {
      if (!amount) return '';
      if (isGstFree) return parseFloat(amount).toFixed(2); // If GST-free, Excl amount equals full gross total
      return (parseFloat(amount) - (parseFloat(amount) / 11)).toFixed(2);
    };

    const isGstFreeSelected = !!values.gstFree;
    const gstValue = calculateGST(values.claimantAmount, isGstFreeSelected);

    const payload = {
      reimbursementId: uuidv4(),
      claimantId: authContext.user?.username || '',
      claimantEmail: authContext.additionalUserInfo?.email || '',
      claimantFullName: authContext.additionalUserInfo?.fullName || '',
      claimedAmount: safe(values.claimantAmount),
      amountExclGST: calculateAmountExclGST(values.claimantAmount, isGstFreeSelected),
      totalGST: gstValue,
      status: 'approved',
      amount: safe(values.claimantAmount),
      extractedAmount: '',
      s3ConnectionUrl: '',
      invoiceDate: '',
      fileName: '',
      createdDate: values.createdDate ? (values.createdDate.format ? values.createdDate.format('YYYY-MM-DD') : values.createdDate) : new Date().toISOString(),
      createdBy: authContext.user?.username || '',
      transactionReference: values.purpose || undefined,
      additionalInfo: safe(values.additionalInfo),
      claimantManagerId: authContext.additionalUserInfo?.reportingManagerId || '',
      documentContent: '',
      supplierName: safe(values.supplierName),
      openAiLargeResponse: '',
      reviewAttachmentDataArray: [],
      description: '',
      purpose: safe(values.purpose),
      attachment: fileList.length > 0 ? fileList[0] : undefined,
      paid: values.paid ? "Y" : "",
      gstFree: isGstFreeSelected ? "Y" : "", // Formats flag string matching "paid" field configuration
      coaDetails: selectedCoaDetails || null,
    };

    try {
      await create(payload);
      message.success('Record saved successfully');
      form.resetFields();
      setFileList([]);
      setSelectedCoaId(null);
      setSelectedCoaDetails(null);
      if (onRecordAdded) {
        onRecordAdded(payload);
      }
    } catch (error) {
      message.error('Error saving record');
      console.error('Error:', error);
    }
  };

  // Restrict purpose dropdown for bills/expenses only
  const billExpensePurposeOptions = [
    { value: 'EXPENSE', label: 'Expense' },
  ];

  // Set initial values on mount
  useEffect(() => {
    form.setFieldsValue(initialValues);
    if (initialValues.coaDetails) {
      setSelectedCoaId(initialValues.coaDetails.id);
      setSelectedCoaDetails(initialValues.coaDetails);
    }
  }, [form, initialValues]);

  return (
    <Form
      form={form}
      layout="inline"
      onFinish={onFinish}
      style={{ marginBottom: 16 }}
      initialValues={initialValues}
    >
      <Form.Item name="createdDate">
        <DatePicker />
      </Form.Item>
      <Form.Item name="claimantAmount">
        <InputNumber min={0} prefix="$" placeholder="Amount" style={{ width: 150 }} />
      </Form.Item>
      <Form.Item name="supplierName">
        <Input placeholder="Supplier Name (optional)" />
      </Form.Item>
      <Form.Item name="purpose">
        <Select
          placeholder="Purpose"
          style={{ width: 200 }}
        >
          {(showAllPurposes ? TRANSACTION_REFERENCE : billExpensePurposeOptions).map(opt => (
            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
          ))}
        </Select>
      </Form.Item>
      
      {/* Type of Account Dropdown */}
      <Form.Item label="Type of Account" required>
        <Select
          showSearch
          placeholder="Select Account"
          style={{ width: 220 }}
          value={selectedCoaId}
          onChange={val => {
            setSelectedCoaId(val);
            const found = chartAccounts.find(acc => acc.id === val);
            setSelectedCoaDetails(found || null);
          }}
          optionFilterProp="children"
          filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
        >
          {chartAccounts.map(acc => (
            <Option key={acc.id} value={acc.id}>{acc.accountName} ({acc.accountType})</Option>
          ))}
        </Select>
      </Form.Item>
      
      <Form.Item name="additionalInfo">
        <Input placeholder="Additional Info (optional)" />
      </Form.Item>
      
      {/* GST Free Checkbox */}
      <Form.Item name="gstFree" valuePropName="checked">
        <Checkbox>GST Free</Checkbox>
      </Form.Item>

      <Form.Item name="paid" valuePropName="checked">
        <Checkbox disabled={paidDisabled}>Paid</Checkbox>
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>Add</Button>
      </Form.Item>
    </Form>
  );
};

export default RecordForm;